import { BlurOn } from "@mui/icons-material";
import { t } from "i18next";
import { FC, useContext, useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { useMapColors, usePageContext } from "@ryde/redux";
import {
  DEFAULT_DRAG_GRIP_COLOR,
  DragTypeEnum,
  generateDragIdGridRow,
  IRideDto,
  IRydeRow,
  isSelectedIdSF,
  isSingleSelectedRideIdSF,
  ridesDraggingDataAtom,
  selectedSingleRowRideIdAtom,
  selectedTableRowsIdsAtomFamily,
  useIsMobile,
} from "@ryde/shared";

import { DraggableWrapper, FramerTooltip } from "../../../../common";
import { CheckboxMui } from "../../../../controls";
import { useTableRowIsSelected } from "../../../generic/table/selection-context";
import { TableWrapperContext } from "../../../generic/table/table-wrapper.context";
import { DragRideRowIconReplacement } from "../cells.ride.styled";

interface ICellMultiIconDragOrSelectHandle {
  rideDto: IRideDto;
  row: IRydeRow<IRideDto>;
}

export const CellMultiIconDragOrSelectHandle: FC<
  ICellMultiIconDragOrSelectHandle
> = ({ rideDto, row }) => {
  const rideId = rideDto.id;
  const { isMobile } = useIsMobile();
  const { entity } = usePageContext();
  const { isGridSingleSelectView } = useContext(TableWrapperContext);

  const isRideBulkSelected = useTableRowIsSelected(entity, rideId);
  const isSingleSelectedRow = useRecoilValue(isSingleSelectedRideIdSF(rideId));
  const setSelectedSingleRowRideId = useSetRecoilState(
    selectedSingleRowRideIdAtom,
  );
  const setRideGridSelectedIds = useSetRecoilState(
    selectedTableRowsIdsAtomFamily(entity),
  );

  const highlightedSingleRow = useMemo(() => {
    return entity === "ride" && isGridSingleSelectView && isSingleSelectedRow;
  }, [entity, isGridSingleSelectView, isSingleSelectedRow]);

  const selectSingleRowForGridMap = useEvent((): void => {
    if (entity !== "ride" || !isGridSingleSelectView) {
      return;
    }
    setSelectedSingleRowRideId(rideId);
    setRideGridSelectedIds([rideId]);
  });
  const isSelected = isRideBulkSelected;
  const setIsSelected = useSetRecoilState(isSelectedIdSF([entity, rideId]));

  const handleSelectToggle = useEvent((): void => {
    setIsSelected(selected => !selected);
  });

  const { getMapColorByRideId } = useMapColors();

  const { type: dragType } = useRecoilValue(ridesDraggingDataAtom);

  const rideRowDraggableCondition = useMemo(() => {
    return dragType !== DragTypeEnum.GRID_PASSENGER;
  }, [dragType]);

  const isTooltipDisabled = useMemo(() => {
    return dragType && dragType !== DragTypeEnum.NOT_DRAGGING;
  }, [dragType]);

  const dragVisibilityCondition = useMemo(() => {
    return !isMobile && rideRowDraggableCondition;
  }, [isMobile, rideRowDraggableCondition]);

  const dragReplacementIconVisibilityCondition = useMemo(() => {
    return !isMobile && !rideRowDraggableCondition;
  }, [isMobile, rideRowDraggableCondition]);

  const gripColor = useMemo(() => {
    return isSelected && !isGridSingleSelectView
      ? getMapColorByRideId(rideId)
      : DEFAULT_DRAG_GRIP_COLOR;
  }, [getMapColorByRideId, isGridSingleSelectView, isSelected, rideId]);

  return (
    <>
      {dragVisibilityCondition && (
        <DraggableWrapper
          id={generateDragIdGridRow(rideId) + "_summary_cell"}
          key={rideId}
          element="div"
          dragData={{
            type: DragTypeEnum.GRID_RIDE,
            payload: { rideDto, cells: row.getVisibleCells() },
          }}
        >
          <FramerTooltip
            tooltipDisabled={isTooltipDisabled}
            content={t("ride.RIDE_DRAG_TOOLTIP")}
          >
            <SVG_MUI.GripVerticalColoredSVG
              className="pointer"
              fill={gripColor}
              data-e2e="drag-grip-vertical"
            />
          </FramerTooltip>
        </DraggableWrapper>
      )}
      {dragReplacementIconVisibilityCondition && (
        <FramerTooltip
          tooltipDisabled={isTooltipDisabled}
          content={t("ride.RIDE_DRAG_TOOLTIP")}
        >
          <DragRideRowIconReplacement
            className="color-circle-multi-select"
            $color={isSelected ? gripColor : undefined}
          />
        </FramerTooltip>
      )}
      {!isGridSingleSelectView && (
        <CheckboxMui
          checked={isSelected}
          onCheckboxToggle={handleSelectToggle}
        />
      )}
      {isGridSingleSelectView && !highlightedSingleRow && (
        <BlurOn
          className="pointer"
          onClick={selectSingleRowForGridMap}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              selectSingleRowForGridMap();
            }
          }}
          role="button"
          tabIndex={0}
        />
      )}
      {isGridSingleSelectView && highlightedSingleRow && (
        <FramerTooltip
          tooltipDisabled={isTooltipDisabled}
          content={t("ride.RIDE_DRAG_TOOLTIP")}
        >
          <DragRideRowIconReplacement
            className="color-circle-single-select"
            $color={getMapColorByRideId(rideId)}
          />
        </FramerTooltip>
      )}
    </>
  );
};
